import { NJBadge, NJButton, NJHeading, NJIcon, NJText } from '@engie-group/fluid-design-system-react';
import { CURRENT_USER } from './homeModel';
import { useProject, useReferential } from './hooks';
import { ProjectMiniMap } from './ProjectMiniMap';
import { DevelopmentPlansTable } from './DevelopmentPlansTable';
import { ProjectAccessAvatars } from './ProjectAccessAvatars';
import type { ProjectSummary } from './types';
import './wireframes.css';
import { EmptyState } from './EmptyState';

function WireframeCard({ project, onOpen, flash }: {
  project: ProjectSummary;
  onOpen: (id: string) => void;
  flash: (message: string) => void;
}) {
  const detail = useProject(project.id);
  const referential = useReferential();
  const month = (value: string) => value.split('-').reverse().join('/');

  return (
    <article className="pcv3-wf-card" aria-label={project.name} data-comment-anchor={`wireframe-card-${project.id}`}>
      <header className="pcv3-wf-card-header">
        <div className="pcv3-wf-heading">
          <NJHeading as="h3" scale="sm">
            <a className="nj-link nj-link--contextual" href="#" onClick={(event) => {
              event.preventDefault();
              onOpen(project.id);
            }}>{project.name}</a>
          </NJHeading>
          <NJText scale="sm" className="pcv3-project-description">{project.description || 'Aucune description renseignée.'}</NJText>
          <div className="pcv3-project-metadata">
            <NJText scale="sm">Créateur : <strong>{project.owner}</strong> · {project.owner === CURRENT_USER ? 'Créé par moi' : 'Partagé avec moi'}</NJText>
            <NJText scale="sm">Dernière modification : {project.updatedAt}</NJText>
          </div>
        </div>
        <div className="pcv3-wf-header-actions">
          <ProjectAccessAvatars project={project} />
          <NJBadge variant="information" emphasis="subtle" scale="sm">En cours</NJBadge>
        </div>
      </header>
      <div className="pcv3-wf-card-body">
        <div className="pcv3-mini-map-slot">
          {detail.data && referential.data ? <ProjectMiniMap detail={detail.data} referential={referential.data} /> : (
            <div className="pcv3-wf-map pcv3-map-message">
              <NJText role={detail.isError || referential.isError ? 'alert' : 'status'}>
                {detail.isError || referential.isError ? 'Impossible de charger la carte du projet.' : 'Chargement de la carte…'}
              </NJText>
            </div>
          )}
        </div>
        <div className="pcv3-wf-facts">
          <div>
            <NJText as="div" scale="sm">Périmètre / commune(s)</NJText>
            <NJText as="strong">{project.communeNames.join(', ')}</NJText>
            {project.perimeterLabel === 'Zone dessinée' && <NJText scale="sm">Zone dessinée dans la commune</NJText>}
          </div>
          <div className="pcv3-wf-tag">
            <div className="pcv3-thermometer"><NJIcon name="thermostat" scale="sm" variant="inherit" aria-hidden="true" /></div>
            <NJText as="span" scale="sm" role={detail.isError ? 'alert' : undefined}>
              {detail.isError ? 'DJU : chargement impossible' : detail.data
                ? `DJU de référence : ${detail.data.project.dju.reference.toLocaleString('fr-FR')}`
                : 'DJU : chargement…'}
            </NJText>
          </div>
          <div>
            <NJText as="div" scale="sm">Type de projet</NJText>
            <NJText>{project.kind === 'new' ? 'Nouveau réseau' : 'Réseaux existants'}</NJText>
          </div>
          <div>
            <NJText as="div" scale="sm">Période du contrat</NJText>
            <NJText>{month(project.contract.start)} → {month(project.contract.end)}</NJText>
          </div>
        </div>
        <DevelopmentPlansTable project={project} flash={flash} />
      </div>
    </article>
  );
}

export function ProjectCardWireframes({ projects, onCreate, onOpen, flash }: {
  projects: ProjectSummary[];
  onCreate: () => void;
  onOpen: (id: string) => void;
  flash: (message: string) => void;
}) {
  const ongoing = projects.filter((project) => !project.archived && !project.completed && !project.parentId
    && (project.owner === CURRENT_USER || project.members.includes(CURRENT_USER)));

  return (
    <section className="pcv3-wireframes" aria-labelledby="pav-ongoing-title">
      <div className="pcv3-section-heading">
        <NJHeading as="h2" scale="md" id="pav-ongoing-title">Mes projets en cours ({ongoing.length})</NJHeading>
        <NJButton label="Nouveau projet" icon="add" onClick={onCreate} />
      </div>
      <div className="pcv3-wf-projects pcv3-wf-grid">
        {ongoing.map((project) => <WireframeCard key={project.id} project={project} onOpen={onOpen} flash={flash} />)}
      </div>
      {ongoing.length === 0 && <EmptyState icon="folder_open" title="Aucun projet en cours">
        <NJText scale="sm">Créez un nouveau projet pour commencer une étude.</NJText>
      </EmptyState>}
    </section>
  );
}
